<template>
  <div class="mod-home">
    <el-alert
      v-if="dueAlert"
      :title="dueAlert"
      type="warning"
      show-icon
      :closable="false"
      style="margin-bottom:16px">
      <el-button type="text" @click="goBoard">打开进度大屏</el-button>
    </el-alert>

    <div class="home-hero">
      <div class="home-hero__title">业务流程引导</div>
      <div class="home-hero__desc">
        点击任一步骤即可打开对应页面（需具备菜单权限）。
      </div>
    </div>

    <el-tabs v-model="activeFlow" type="card" @tab-click="persistFlow">
      <el-tab-pane
        v-for="flow in flows"
        :key="flow.id"
        :label="flow.title"
        :name="flow.id">
        <div class="flow-intro">{{ flow.desc }}</div>
        <div class="flow-steps">
          <template v-for="(step, index) in flow.steps">
            <div
              :key="step.path"
              class="step-card"
              :class="{ 'is-disabled': !hasPath(step.path) }"
              @click="goStep(step)">
              <div class="step-card__head">
                <span class="step-card__index">{{ index + 1 }}</span>
                <span v-if="!hasPath(step.path)" class="step-card__lock">无权限</span>
                <span v-else class="step-card__role">{{ step.role }}</span>
              </div>
              <div class="step-card__title">{{ step.title }}</div>
              <div class="step-card__hint">{{ step.hint }}</div>
              <div class="step-card__action">
                {{ hasPath(step.path) ? '点击进入 ›' : '请联系管理员开通菜单' }}
              </div>
            </div>
            <div
              v-if="index < flow.steps.length - 1"
              :key="step.path + '-arrow'"
              class="step-arrow"
              aria-hidden="true">→</div>
          </template>
        </div>
      </el-tab-pane>
    </el-tabs>

    <div class="home-tips">
      <div class="home-tips__title">使用提示</div>
      <ul>
        <li>灰色步骤表示当前账号没有对应菜单权限，或菜单尚未配置。</li>
        <li>日常生产建议按「生产执行」顺序：工单 → 工序任务 → 报工 → 异常。</li>
        <li>首次上线先走「首次主数据」，再开工单。</li>
        <li>左侧菜单仍可直接进入任意功能；本页仅作流程导航。</li>
      </ul>
    </div>
  </div>
</template>

<script>
import { fetchNav } from '../api/auth'
import { progressBoard } from '../api/productionOrder'
import { hasMenuAccess } from '../utils/nav'

const FLOW_STORAGE_KEY = 'mesHomeActiveFlow'

const FLOWS = [
  {
    id: 'master',
    title: '首次主数据',
    desc: '系统上线或新产品导入时，先维护主数据，再开生产。',
    steps: [
      { title: '产品', hint: '维护物料/成品编码与单位', role: '工艺/计划', path: '/mes/product' },
      { title: '工序', hint: '定义可复用的标准工序', role: '工艺', path: '/mes/operation' },
      { title: '工艺', hint: '产品工艺路线与投料/产出', role: '工艺', path: '/mes/technology' },
      { title: '车间', hint: '维护车间/分部组织', role: '基础数据', path: '/mes/division' },
      { title: '产线', hint: '产线与设备归属', role: '基础数据', path: '/mes/productionLine' },
      { title: '仓库', hint: '原料仓、成品仓等', role: '仓储', path: '/warehouse/ware-house' },
      { title: '员工', hint: '车间人员，供 App 绑定报工', role: '人事/车间', path: '/mes/staff' }
    ]
  },
  {
    id: 'production',
    title: '生产执行',
    desc: '从工单到报工确认的日常生产主线。报工确认后会自动领料与生产入库。',
    steps: [
      { title: '主订单', hint: '可选：销售主生产订单', role: '计划', path: '/sales/master-order' },
      { title: '生产工单', hint: '创建并受理/开工工单', role: '计划/车间', path: '/production/order' },
      { title: '进度大屏', hint: '看当前工序，临期和逾期会标出', role: '车间/计划', path: '/production/progress-board' },
      { title: '物料需求', hint: '按工单展开用料，可备料', role: '计划/仓储', path: '/production/material-requirement' },
      { title: '工序任务', hint: '按工艺生成任务并开工', role: '车间', path: '/production/order-task' },
      { title: '生产报工', hint: '登记合格/不良并确认。车间扫码在 App 工作台', role: '车间', path: '/production/tracking' },
      { title: '报工异常', hint: '处理报废与质量异常', role: '质量/车间', path: '/production/tracking-anomaly' }
    ]
  },
  {
    id: 'warehouse',
    title: '仓储进出',
    desc: '日常库存查询、单据执行、盘点与重包装。',
    steps: [
      { title: '库存查询', hint: '按仓库/批次查看现存量', role: '仓储', path: '/inventory/quantity-search' },
      { title: '库存单据', hint: '领料、入库、调拨等执行', role: '仓储', path: '/inventory/stock-document' },
      { title: '盘点', hint: '盘点单录入与过账', role: '仓储', path: '/inventory/stocktaking' },
      { title: '重包装', hint: '批次/托盘重包装过账', role: '仓储', path: '/inventory/repacking' }
    ]
  },
  {
    id: 'purchase',
    title: '采购到货',
    desc: '采购到货登记并入库确认后增加现存量。',
    steps: [
      { title: '采购到货', hint: '登记供应商到货并入库确认', role: '采购/仓储', path: '/supply/purchase-delivery' },
      { title: '库存单据', hint: '查看自动生成的采购入库单', role: '仓储', path: '/inventory/stock-document' },
      { title: '库存查询', hint: '核对入库后库存', role: '仓储', path: '/inventory/quantity-search' }
    ]
  }
]

export default {
  name: 'Home',
  data() {
    const saved = sessionStorage.getItem(FLOW_STORAGE_KEY)
    const valid = FLOWS.some(f => f.id === saved)
    return {
      activeFlow: valid ? saved : 'production',
      flows: FLOWS,
      menus: [],
      dueAlert: ''
    }
  },
  created() {
    this.refreshMenus()
    this.loadDue()
  },
  activated() {
    this.refreshMenus()
    this.loadDue()
  },
  methods: {
    refreshMenus() {
      fetchNav().then(res => {
        this.menus = res.data || []
      }).catch(() => {
        this.menus = []
      })
    },
    hasPath(path) {
      return hasMenuAccess(this.menus, { path })
    },
    persistFlow() {
      sessionStorage.setItem(FLOW_STORAGE_KEY, this.activeFlow)
    },
    loadDue() {
      progressBoard().then(res => {
        const summary = (res.data && res.data.summary) || {}
        const overdue = Number(summary.overdue) || 0
        const dueSoon = Number(summary.dueSoon) || 0
        if (!overdue && !dueSoon) {
          this.dueAlert = ''
          return
        }
        const parts = []
        if (overdue) parts.push(overdue + ' 张工单已逾期')
        if (dueSoon) parts.push(dueSoon + ' 张工单两天内到期')
        this.dueAlert = parts.join('，')
      }).catch(() => {
        this.dueAlert = ''
      })
    },
    goBoard() {
      this.$router.push('/production/progress-board').catch(() => {})
    },
    goStep(step) {
      if (!this.hasPath(step.path)) {
        this.$message.warning('无菜单权限或未配置：' + step.title)
        return
      }
      this.$router.push(step.path).catch(() => {})
    }
  }
}
</script>

<style scoped>
.mod-home {
  padding: 8px 4px 24px;
  line-height: 1.5;
  color: #303133;
}

.home-hero {
  margin-bottom: 18px;
}

.home-hero__title {
  font-size: 22px;
  font-weight: 600;
  color: #1f2d3d;
}

.home-hero__desc {
  margin-top: 8px;
  font-size: 14px;
  color: #606266;
  max-width: 920px;
}

.flow-intro {
  margin: 4px 0 16px;
  font-size: 13px;
  color: #909399;
}

.flow-steps {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 8px 0;
}

.step-card {
  width: 168px;
  min-height: 148px;
  padding: 14px 14px 12px;
  margin: 4px 0;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
}

.step-card:hover {
  border-color: #409eff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.15);
  transform: translateY(-1px);
}

.step-card.is-disabled {
  background: #f5f7fa;
  border-color: #ebeef5;
  cursor: not-allowed;
  opacity: 0.78;
}

.step-card.is-disabled:hover {
  border-color: #ebeef5;
  box-shadow: none;
  transform: none;
}

.step-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.step-card__index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #ecf5ff;
  color: #409eff;
  font-size: 13px;
  font-weight: 700;
}

.step-card.is-disabled .step-card__index {
  background: #e9e9eb;
  color: #909399;
}

.step-card__role,
.step-card__lock {
  font-size: 12px;
  color: #909399;
}

.step-card__lock {
  color: #f56c6c;
}

.step-card__title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 6px;
}

.step-card__hint {
  font-size: 12px;
  color: #606266;
  min-height: 36px;
}

.step-card__action {
  margin-top: 10px;
  font-size: 12px;
  color: #409eff;
}

.step-card.is-disabled .step-card__action {
  color: #c0c4cc;
}

.step-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  color: #c0c4cc;
  font-size: 18px;
  font-weight: 600;
  user-select: none;
}

.home-tips {
  margin-top: 28px;
  padding: 14px 16px;
  background: #f5f7fa;
  border-radius: 8px;
  border: 1px solid #ebeef5;
}

.home-tips__title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
}

.home-tips ul {
  margin: 0;
  padding-left: 18px;
  color: #606266;
  font-size: 13px;
}

.home-tips li {
  margin: 4px 0;
}

@media (max-width: 1100px) {
  .step-card {
    width: 150px;
  }
}

@media (max-width: 768px) {
  .step-arrow {
    display: none;
  }
  .step-card {
    width: calc(50% - 8px);
    margin-right: 8px;
  }
}
</style>
